import { defineComponent, h, ref, computed, watch, inject, toRef } from 'vue';
import { QInput } from 'quasar';
import {
    VARIABLE_BINDING_INJECT_KEY,
    parseBinding,
    formatBinding,
    parseVariableTarget,
    formatVariableTarget,
    schemaAllowsVariableBinding,
    schemaAllowsVariableTarget,
    schemaVariableTypeHint,
    isSimpleVariableBinding,
    typesCompatible,
    ASSIGNED_VARIABLE_TOOLTIP,
    unwrapVariableBindingContext,
    showEssentialsExpressionChip,
} from './variableBinding.js';
import {
    buildVariablePickerOptions,
    renderVariablePill,
    renderVariablePickerMenu,
    renderVariableAssistIcon,
    renderVariableAssignCta,
    renderExpressionChip,
} from './variableBindingUi.jsx';

/**
 * Provides variableBindingContext to descendants (SchemaForm + UserControl trees).
 * Use around essential property editors in FlowV2.
 */
export const VariableBindingProvider = defineComponent({
    name: 'VariableBindingProvider',
    props: {
        context: { type: Object, default: null },
    },
    provide() {
        return {
            [VARIABLE_BINDING_INJECT_KEY]: toRef(this, 'context'),
        };
    },
    setup(props, { slots }) {
        return () => slots.default?.() ?? null;
    },
});

function schemaForLiteralCheck(props) {
    const schema = props.schema && typeof props.schema === 'object' ? { ...props.schema } : {};
    if (props.allowVariableTarget === true)
        schema.AllowVariableTarget = true;
    if (!schema.type && props.inputType === 'number')
        schema.type = 'number';
    if (!schema.type && (props.inputType === 'checkbox' || props.inputType === 'boolean'))
        schema.type = 'boolean';
    return schema;
}

function useVariableBindingFieldState(props, emit) {
    const injected = inject(VARIABLE_BINDING_INJECT_KEY, null);
    const hovered = ref(false);
    const focused = ref(false);
    const modeOverride = ref(null); // null | 'literal' | 'variable'
    const literalScratch = ref(undefined);
    const filter = ref('');
    const pickerOpen = ref(false);

    const ctx = computed(() => unwrapVariableBindingContext(injected));
    const hostEnabled = computed(() => !!(ctx.value && ctx.value.enabled));
    const schemaAllows = computed(() =>
        props.allowVariable === true
        || schemaAllowsVariableBinding(props.schema));
    const schemaAllowsTarget = computed(() =>
        props.allowVariableTarget === true
        || schemaAllowsVariableTarget(props.schema));
    const isTargetMode = computed(() => schemaAllowsTarget.value);
    const pickerMode = computed(() => (isTargetMode.value ? 'target' : 'use'));

    /** Pill+clear when already bound; assist when schema/prop allows. */
    const enabled = computed(() =>
        hostEnabled.value
        && (schemaAllows.value
            || schemaAllowsTarget.value
            || isSimpleVariableBinding(props.modelValue)));
    const assistAllowed = computed(() =>
        hostEnabled.value && (schemaAllows.value || schemaAllowsTarget.value));

    const boundName = computed(() => {
        if (isTargetMode.value)
            return parseVariableTarget(props.modelValue);
        return parseBinding(props.modelValue);
    });
    const isVariableMode = computed(() => {
        if (!enabled.value)
            return false;
        if (modeOverride.value === 'variable')
            return true;
        if (modeOverride.value === 'literal')
            return false;
        return boundName.value != null;
    });

    const isExpressionMode = computed(() => {
        if (isVariableMode.value)
            return false;
        return showEssentialsExpressionChip(
            hostEnabled.value,
            props.modelValue,
            schemaForLiteralCheck(props),
            {
                allowCustomInput: !!(props.schema?.formatData?.AllowCustomInput),
            },
        );
    });

    const showAssistChrome = computed(() =>
        assistAllowed.value
        && !isTargetMode.value
        && !isVariableMode.value
        && !isExpressionMode.value
        && (hovered.value || focused.value || pickerOpen.value));

    const typeHint = computed(() => schemaVariableTypeHint(props.schema));

    function pickerOptions() {
        const mode = pickerMode.value;
        const symbols = typeof ctx.value?.getPickerSymbols === 'function'
            ? ctx.value.getPickerSymbols(mode)
            : (ctx.value?.variables || []);
        return buildVariablePickerOptions(symbols, filter.value, {
            expectedType: typeHint.value,
            mode,
        });
    }

    watch(() => props.modelValue, (v) => {
        if (modeOverride.value == null)
            return;
        if (modeOverride.value === 'literal') {
            if (isTargetMode.value ? parseVariableTarget(v) : parseBinding(v))
                modeOverride.value = null;
        }
    });

    function openPicker() {
        if (!assistAllowed.value && !isVariableMode.value)
            return;
        if (!isTargetMode.value
            && boundName.value == null
            && literalScratch.value === undefined)
            literalScratch.value = props.modelValue;
        filter.value = boundName.value || '';
        pickerOpen.value = true;
        ctx.value?.notifyUseVariable?.({
            action: 'open-picker',
            value: props.modelValue,
            target: !!isTargetMode.value,
        });
        emit('use-variable', { action: 'open-picker' });
    }

    function closePicker() {
        pickerOpen.value = false;
        filter.value = '';
    }

    function exitVariableMode() {
        closePicker();
        if (isTargetMode.value) {
            modeOverride.value = null;
            literalScratch.value = undefined;
            emit('update:modelValue', '');
            emit('clear-variable');
            return;
        }
        modeOverride.value = 'literal';
        const restore = literalScratch.value !== undefined ? literalScratch.value : '';
        literalScratch.value = undefined;
        emit('update:modelValue', restore);
        emit('clear-variable');
    }

    function clearExpression() {
        closePicker();
        modeOverride.value = isTargetMode.value ? null : 'literal';
        literalScratch.value = undefined;
        const schema = props.schema;
        const restore = schema && Object.prototype.hasOwnProperty.call(schema, 'default')
            ? schema.default
            : '';
        emit('update:modelValue', restore);
        emit('clear-expression');
    }

    function findSymbol(name) {
        const mode = pickerMode.value;
        const symbols = typeof ctx.value?.getPickerSymbols === 'function'
            ? ctx.value.getPickerSymbols(mode)
            : (ctx.value?.variables || []);
        const key = String(name || '').toLowerCase();
        return symbols.find(s => s && s.Name && String(s.Name).toLowerCase() === key) || null;
    }

    function commitVariableName(name, { create } = {}) {
        if (!assistAllowed.value && !isVariableMode.value)
            return;
        const trimmed = (name == null ? '' : String(name)).trim();
        if (!trimmed)
            return;

        const mode = pickerMode.value;
        if (!create) {
            const sym = findSymbol(trimmed);
            if (sym && !typesCompatible(sym.Type, typeHint.value, { mode }))
                return;
        }

        let resolved = trimmed;
        if (create && ctx.value?.createVariable) {
            resolved = ctx.value.createVariable(trimmed, typeHint.value);
            if (!resolved)
                return;
        }
        else if (create && !ctx.value?.createVariable) {
            emit('create-variable', trimmed);
        }
        modeOverride.value = 'variable';
        const stored = isTargetMode.value
            ? formatVariableTarget(resolved)
            : formatBinding(resolved);
        if (!stored)
            return;
        emit('update:modelValue', stored);
        emit('use-variable', resolved);
        ctx.value?.notifyUseVariable?.({
            action: 'bind',
            name: resolved,
            target: !!isTargetMode.value,
        });
        closePicker();
    }

    function renderPicker() {
        return renderVariablePickerMenu({
            modelValue: pickerOpen.value,
            onUpdateOpen: (v) => {
                pickerOpen.value = !!v;
                if (!v)
                    filter.value = '';
            },
            filter: filter.value,
            onUpdateFilter: (v) => { filter.value = v ?? ''; },
            options: pickerOptions(),
            onPick: (n, opts) => commitVariableName(n, opts),
            onCreate: (n) => commitVariableName(n, { create: true }),
            expectedType: typeHint.value,
            mode: pickerMode.value,
        });
    }

    return {
        enabled,
        assistAllowed,
        isTargetMode,
        ctx,
        hovered,
        focused,
        isVariableMode,
        isExpressionMode,
        showAssistChrome,
        variableOptions: pickerOptions,
        boundName,
        filter,
        pickerOpen,
        openPicker,
        closePicker,
        exitVariableMode,
        clearExpression,
        commitVariableName,
        renderPicker,
    };
}

/**
 * Dual-mode Quasar field for Flow essentials and custom UserControl forms.
 * Literal: normal input + "{}" opens a pick/create menu.
 * Bound: variable pill replaces the input entirely.
 * Target (`allowVariableTarget`): empty assign CTA → bare name pill (no free-text).
 * Expression: amber chip with Clear when the FlowV2 host is on and the value is not a typed literal.
 */
export default defineComponent({
    name: 'VariableBindableField',
    components: { QInput },
    props: {
        modelValue: { default: null },
        label: { type: String, default: undefined },
        hint: { type: String, default: undefined },
        rules: { type: Array, default: undefined },
        dense: { type: Boolean, default: true },
        outlined: { type: Boolean, default: true },
        /** When no default slot, render this Quasar input type for literal mode. */
        inputType: { type: String, default: 'text' },
        disable: { type: Boolean, default: false },
        /** Strict opt-in for `{}` assist (or pass schema with AllowVariable / formatData.AllowVariable). */
        allowVariable: { type: Boolean, default: false },
        /** Opt-in for Output / ReturnVar save-target (bare name, assign CTA). */
        allowVariableTarget: { type: Boolean, default: false },
        /** Optional field schema; checked via schemaAllowsVariableBinding / Target. */
        schema: { type: Object, default: null },
    },
    emits: [
        'update:modelValue',
        'use-variable',
        'create-variable',
        'clear-variable',
        'clear-expression',
        'focus',
        'blur',
    ],
    setup(props, { emit, slots }) {
        const state = useVariableBindingFieldState(props, emit);

        return () => {
            // Expression chip requires the FlowV2 host; per-field AllowVariable is not required.
            if (state.isExpressionMode.value) {
                return (
                    <div
                        class="c-variable-bindable-field c-essentials-expression-field"
                        style={{ width: '100%' }}
                    >
                        {renderExpressionChip({
                            expression: props.modelValue,
                            label: props.label,
                            disable: props.disable,
                            onClear: () => state.clearExpression(),
                        })}
                    </div>
                );
            }

            if (!state.enabled.value) {
                if (slots.default)
                    return slots.default({ variableMode: false, append: null });
                return (
                    <QInput
                        type={props.inputType}
                        modelValue={props.modelValue}
                        onUpdate:modelValue={(v) => emit('update:modelValue', v)}
                        label={props.label}
                        hint={props.hint}
                        rules={props.rules}
                        dense={props.dense}
                        outlined={props.outlined}
                        disable={props.disable}
                        class="sform-input-white caption"
                        style={{ width: '100%' }}
                    />
                );
            }

            if (state.isVariableMode.value && state.boundName.value) {
                return (
                    <div
                        class="c-variable-bindable-field c-variable-bindable-field--variable"
                        style={{ width: '100%' }}
                    >
                        {renderVariablePill({
                            name: state.boundName.value,
                            label: props.label,
                            disable: props.disable,
                            onClear: () => state.exitVariableMode(),
                            onChange: () => state.openPicker(),
                            menu: state.renderPicker(),
                            clearTooltip: state.isTargetMode.value
                                ? ASSIGNED_VARIABLE_TOOLTIP
                                : undefined,
                        })}
                    </div>
                );
            }

            if (state.assistAllowed.value && state.isTargetMode.value) {
                return (
                    <div
                        class="c-variable-bindable-field c-variable-bindable-field--assign"
                        style={{ width: '100%' }}
                    >
                        {renderVariableAssignCta({
                            label: props.label,
                            disable: props.disable,
                            onOpenPicker: () => state.openPicker(),
                            menu: state.renderPicker(),
                        })}
                    </div>
                );
            }

            if (!state.assistAllowed.value) {
                if (slots.default)
                    return slots.default({ variableMode: false, append: null });
                return (
                    <QInput
                        type={props.inputType}
                        modelValue={props.modelValue}
                        onUpdate:modelValue={(v) => emit('update:modelValue', v)}
                        label={props.label}
                        hint={props.hint}
                        rules={props.rules}
                        dense={props.dense}
                        outlined={props.outlined}
                        disable={props.disable}
                        class="sform-input-white caption"
                        style={{ width: '100%' }}
                    />
                );
            }

            const append = () => renderVariableAssistIcon({
                visible: state.showAssistChrome.value,
                pickerOpen: state.pickerOpen.value,
                onOpenPicker: () => state.openPicker(),
                menu: state.renderPicker(),
            });

            const hoverHandlers = {
                onMouseenter: () => { state.hovered.value = true; },
                onMouseleave: () => { state.hovered.value = false; },
            };

            if (slots.default) {
                return (
                    <div class="c-variable-bindable-field" {...hoverHandlers}>
                        {slots.default({
                            variableMode: false,
                            append,
                            showAssist: state.showAssistChrome.value,
                            onFocus: () => {
                                state.focused.value = true;
                                emit('focus');
                            },
                            onBlur: () => {
                                state.focused.value = false;
                                emit('blur');
                            },
                            openVariablePicker: state.openPicker,
                        })}
                    </div>
                );
            }

            return (
                <div class="c-variable-bindable-field" {...hoverHandlers}>
                    <QInput
                        type={props.inputType}
                        modelValue={props.modelValue}
                        onUpdate:modelValue={(v) => emit('update:modelValue', v)}
                        label={props.label}
                        hint={props.hint}
                        rules={props.rules}
                        dense={props.dense}
                        outlined={props.outlined}
                        disable={props.disable}
                        class="sform-input-white caption"
                        style={{ width: '100%' }}
                        onFocus={() => {
                            state.focused.value = true;
                            emit('focus');
                        }}
                        onBlur={() => {
                            state.focused.value = false;
                            emit('blur');
                        }}
                    >
                        {{ append }}
                    </QInput>
                </div>
            );
        };
    },
});

export { useVariableBindingFieldState };
